import React from 'react';
import { Button, Typography, Box, IconButton, Paper, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ButtonProps, TypographyProps, BoxProps, IconButtonProps, PaperProps, LinkProps } from '@mui/material';

// Styled Button Components
export const PrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#00573D',
  color: '#fff',
  borderRadius: 0,
  fontWeight: 700,
  border: 'none',
  fontSize: '0.75rem',
  padding: '12px 16px',
  minHeight: '48px',
  '&:hover': {
    backgroundColor: '#004B34',
  },
  '&:focus-visible, &:focus': {
    outline: 'none',
  },
}));

export const SecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#00573D',
  border: '1.5px solid #00573D',
  borderRadius: 0,
  fontWeight: 700,
  padding: '12px 16px',
  minHeight: '48px',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#003d29',
    color: '#003d29',
  },
}));

export const ErrorButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#E94B5A',
  color: '#fff',
  border: 'none',
  borderRadius: '4px',
  padding: '8px 16px',
  '&:hover': {
    backgroundColor: '#d63447',
  },
}));

export const FilterButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#00573D',
  color: '#fff',
  fontWeight: 700,
  borderRadius: 0,
  padding: '16px 40px',
  fontSize: 16,
  letterSpacing: 1,
  boxShadow: 'none',
  height: '100%',
  '&:hover': {
    backgroundColor: '#00462e',
  },
  '&.disabled': {
    backgroundColor: '#C9CACC',
    '&:hover': {
      backgroundColor: '#C9CACC',
    },
  },
}));

// Styled Typography Components
export const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '36px',
  fontWeight: 300,
  color: '#1e293b',
  fontFamily: 'Noto Sans, Inter, Segoe UI, Arial, sans-serif',
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Noto Sans, Inter, Segoe UI, Arial, sans-serif',
  fontWeight: 700,
  fontSize: '14px',
  lineHeight: '16px',
  letterSpacing: '0.56px',
  textTransform: 'uppercase',
  color: '#262728',
}));

export const BodyText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Noto Sans, Inter, Segoe UI, Arial, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: 0,
  color: '#262728',
}));

export const MutedText = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: '#64748b',
  fontSize: '14px',
}));

export const PrimaryLink = styled(Typography)(({ theme }) => ({
  color: '#00805A',
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': {
    color: '#00805A',
  },
}));

// Styled Link Components
export const SuccessLink = styled(Link)(({ theme }) => ({
  color: '#00573D',
  textDecoration: 'none',
  '&:hover': {
    color: '#00573D',
    textDecoration: 'underline',
  },
}));

// Styled Layout Components  
export const FlexBetween = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

export const FlexCenter = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const FlexStart = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
}));

export const FlexEnd = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
}));

export const FlexColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

// Styled Icon Button
export const NoOutlineIconButton = styled(IconButton)(({ theme }) => ({
  '&:focus-visible, &:focus': {
    outline: 'none',
  },
}));

// Filter Chip Component
export const FilterChip = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  border: '1.5px solid #00573D',
  borderRadius: 0,
  padding: '4px 12px',
  marginRight: '8px',
  background: '#fff',
  height: 26,
}));

export const FilterChipText = styled(Typography)(({ theme }) => ({
  color: '#00573D',
  fontFamily: 'Noto Sans, Inter, Segoe UI, Arial, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: 0,
  marginRight: '8px',
}));

// Status Dot Component
interface StatusDotProps {
  active?: boolean;
}

export const StatusDot = styled('span')<StatusDotProps>(({ theme, active }) => ({
  display: 'inline-block',
  width: 12,
  height: 12,
  borderRadius: '50%',
  background: active ? '#00805A' : '#BDBDBD',
  marginRight: 8,
  verticalAlign: 'middle',
}));

// Card Components
export const ElevatedCard = styled(Paper)(({ theme }) => ({
  boxShadow: '0px 8px 24px 0px rgba(0,0,0,0.12)',
  transition: 'box-shadow 0.2s ease-in-out',
  borderRadius: 0,
  '&:hover': {
    boxShadow: '0px 12px 32px 0px rgba(0,0,0,0.16)',
  },
}));

export const DropdownCard = styled(Paper)(({ theme }) => ({
  boxShadow: '0px 8px 24px 0px rgba(0,0,0,0.12)',
  borderRadius: 0,
  borderTop: '4px solid #00573D',
  transition: 'border-color 0.2s',
}));

// Form Components
export const SearchTextField = styled('div')(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#00573D',
    },
  },
  '& .MuiInputBase-input': {
    fontFamily: 'Noto Sans, Inter, Segoe UI, Arial, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: 0,
    '&::placeholder': {
      fontStyle: 'italic',
      color: 'rgba(0,0,0,0.6)',
      opacity: 1,
    },
  },
}));

// Tab Components
export const StyledTab = {
  fontWeight: 600,
  borderRadius: 0,
  '&.active': {
    borderTop: '2px solid #004B8D',
    color: '#004B8D',
    backgroundColor: 'transparent',
  },
  '&.inactive': {
    backgroundColor: '#F3F3F3',
    color: '#333',
  },
};

// Accordion Components
export const AccordionExpandIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  '&:focus-visible, &:focus': {
    outline: 'none',
  },
}));

// Filter Dropdown Components
export const FilterDropdownCollapsed = styled(Box)(({ theme }) => ({
  width: '100%',
  borderRadius: 0,
  borderTop: '2px solid #C9CACC',
  background: '#fff',
  cursor: 'pointer',
  padding: '12px 16px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  userSelect: 'none',
  color: '#262728',
  [theme.breakpoints.down('sm')]: {
    padding: '12px 0px',
  },
}));

export const FilterDropdownExpanded = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  borderBottom: '1px solid',
  borderColor: 'divider',
  userSelect: 'none',
}));

export const FilterFormGroup = styled(Box)(({ theme }) => ({
  padding: '8px 16px 16px 16px',
}));